import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import axios from 'axios';
import './Wallet.css';

const Wallet = () => {
    const { user, logout, getFreshToken } = useAuth();
    const [walletData, setWalletData] = useState(null);
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('statement'); // 'statement' | 'redeem'

    // Modals
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showRedeemModal, setShowRedeemModal] = useState(false);
    const [selectedReward, setSelectedReward] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false); // Added for photo upload in sidebar

    // Form States - Receipt
    const [purchaseValue, setPurchaseValue] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [storeName, setStoreName] = useState('');
    const [receiptImage, setReceiptImage] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Form States - Redemption
    const [pixKey, setPixKey] = useState('');
    const [redeemDetails, setRedeemDetails] = useState('');

    useEffect(() => {
        fetchWalletData();
        fetchRewards();
        checkDailyReward();
    }, []);

    const fetchWalletData = async () => {
        try {
            const token = await getFreshToken();
            if (!token) return;

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/wallet/data`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWalletData(response.data);
        } catch (error) {
            console.error("Erro ao carregar carteira:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRewards = async () => {
        try {
            const token = await getFreshToken();
            if (!token) return;

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/wallet/rewards`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRewards(response.data);
        } catch (error) {
            console.error("Erro ao carregar prêmios:", error);
        }
    };

    const checkDailyReward = async () => {
        try {
            const token = await getFreshToken();
            if (!token) return;

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/wallet/daily-reward`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.rewarded) {
                alert(`🎉 ${response.data.message} (+${response.data.amount} moedas)`);
                fetchWalletData(); // Atualiza saldo
            }
        } catch (error) {
            console.error("Erro ao checar recompensa diária:", error);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        try {
            setUploading(true);
            const token = await getFreshToken();
            if (!token) return;

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });
            setReceiptImage(response.data.imageUrl);
        } catch (error) {
            console.error("Erro no upload:", error);
            alert("Erro ao enviar imagem.");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmitReceipt = async (e) => {
        e.preventDefault();
        if (!receiptImage) return alert("Por favor, envie a foto do comprovante.");

        try {
            const token = await getFreshToken();
            if (!token) return;

            await axios.post(`${process.env.REACT_APP_API_URL}/wallet/receipts`, {
                imageUrl: receiptImage,
                purchaseValue,
                purchaseDate,
                storeName
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert("Nota enviada com sucesso! Aguarde a aprovação.");
            setShowUploadModal(false);
            setPurchaseValue('');
            setPurchaseDate('');
            setStoreName('');
            setReceiptImage(null);
        } catch (error) {
            console.error("Erro ao enviar nota:", error);
            alert("Erro ao registrar nota.");
        }
    };

    const handleRedeemClick = (reward) => {
        if ((walletData?.wallet?.balance || 0) < reward.cost) {
            return alert("Saldo insuficiente para este prêmio.");
        }
        setSelectedReward(reward);
        setShowRedeemModal(true);
    };

    const confirmRedemption = async () => {
        if (!selectedReward) return;

        try {
            const token = await getFreshToken();
            if (!token) return;

            await axios.post(`${process.env.REACT_APP_API_URL}/wallet/redeem`, {
                rewardId: selectedReward.id,
                pixKey,
                details: redeemDetails
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert("Resgate solicitado com sucesso! Em breve você receberá seu prêmio.");
            setShowRedeemModal(false);
            setPixKey('');
            setRedeemDetails('');
            fetchWalletData(); // Atualiza saldo
        } catch (error) {
            console.error("Erro no resgate:", error);
            alert(error.response?.data?.error || "Erro ao processar resgate.");
        }
    };

    if (loading) return <div className="loading">Carregando carteira...</div>;

    const menuItems = [
        { id: "dados", label: "Meus Dados", icon: "fas fa-user", path: "/perfil" },
        { id: "carteira", label: "Minha Carteira", icon: "fas fa-wallet", path: "/carteira" },
        { id: "seguranca", label: "Segurança", icon: "fas fa-lock", path: "/perfil" },
        { id: "pedidos", label: "Meus Pedidos", icon: "fas fa-box", path: "/perfil" },
        { id: "favoritos", label: "Meus Favoritos", icon: "fas fa-heart", path: "/perfil" },
    ];

    return (
        <div className="container" style={{ padding: "var(--spacing-lg) var(--spacing-md)" }}>
            <h1 style={{ marginBottom: "var(--spacing-lg)", fontSize: "24px" }}>Minha Carteira</h1>

            <div className="grid grid-cols-1 grid-md-4" style={{ gap: "var(--spacing-lg)", gridTemplateColumns: "250px 1fr" }}>
                {/* Sidebar Menu */}
                <div style={{
                    backgroundColor: "var(--white)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-sm)",
                    overflow: "hidden",
                    height: "fit-content"
                }}>
                    <div style={{ padding: "20px", borderBottom: "1px solid var(--border-color)", textAlign: "center" }}>
                        <div style={{
                            width: "60px",
                            height: "60px",
                            backgroundColor: "var(--primary-color)",
                            color: "white",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "24px",
                            margin: "0 auto 10px",
                            overflow: "hidden",
                            position: "relative"
                        }}>
                            {user?.photoURL ? (
                                <img src={user.photoURL} alt={user.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                                user?.email?.charAt(0).toUpperCase()
                            )}
                        </div>
                        <div style={{ fontWeight: "bold", wordBreak: "break-all" }}>{user?.name || user?.email?.split("@")[0]}</div>
                        <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{user?.email}</div>
                    </div>

                    <nav>
                        {menuItems.map((item) => (
                            <Link
                                key={item.id}
                                to={item.path}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "15px 20px",
                                    border: "none",
                                    backgroundColor: item.id === 'carteira' ? "rgba(52, 131, 250, 0.1)" : "transparent",
                                    color: item.id === 'carteira' ? "var(--primary-color)" : "var(--text-primary)",
                                    fontWeight: item.id === 'carteira' ? "bold" : "normal",
                                    borderLeft: item.id === 'carteira' ? "4px solid var(--primary-color)" : "4px solid transparent",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    fontSize: "14px",
                                    textDecoration: "none"
                                }}
                            >
                                <i className={item.icon} style={{ width: "20px" }}></i> {item.label}
                            </Link>
                        ))}
                        <button
                            onClick={logout}
                            style={{
                                width: "100%",
                                textAlign: "left",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "15px 20px",
                                color: "#d32f2f",
                                background: "none",
                                border: "none",
                                fontSize: "14px",
                                borderTop: "1px solid var(--border-color)",
                                cursor: "pointer"
                            }}
                        >
                            <i className="fas fa-sign-out-alt" style={{ width: "20px" }}></i> Sair
                        </button>
                    </nav>
                </div>

                {/* Content Area */}
                <div className="wallet-content-wrapper">
                    <div className="wallet-header-card">
                        <div className="balance-card">
                            <span className="balance-label">Saldo Atual</span>
                            <div className="balance-value">
                                <i className="fas fa-coins"></i>
                                {walletData?.wallet?.balance || 0}
                            </div>
                            <span className="balance-unit">Ofertiz Coins</span>
                        </div>

                        <div className="wallet-actions">
                            <button
                                className={`btn-tab ${activeTab === 'statement' ? 'active' : ''}`}
                                onClick={() => setActiveTab('statement')}
                            >
                                Extrato
                            </button>
                            <button
                                className={`btn-tab ${activeTab === 'redeem' ? 'active' : ''}`}
                                onClick={() => setActiveTab('redeem')}
                            >
                                Resgatar Prêmios
                            </button>
                        </div>
                    </div>

                    <div className="wallet-body">
                        {activeTab === 'statement' ? (
                            <div className="transactions-section">
                                <div className="section-header">
                                    <h2>Histórico de Transações</h2>
                                    <button className="btn-upload-receipt" onClick={() => setShowUploadModal(true)}>
                                        <i className="fas fa-receipt"></i> Enviar Nota (+5.000)
                                    </button>
                                </div>

                                <div className="transactions-list">
                                    {walletData?.transactions?.length > 0 ? (
                                        walletData.transactions.map(tx => (
                                            <div key={tx.id} className={`transaction-item ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                                                <div className="tx-icon">
                                                    <i className={`fas ${tx.type === 'daily_login' ? 'fa-calendar-check' :
                                                        tx.type === 'offer_click' ? 'fa-mouse-pointer' :
                                                            tx.type === 'receipt_approved' ? 'fa-check-circle' :
                                                                tx.type === 'redemption' ? 'fa-gift' :
                                                                    'fa-exchange-alt'
                                                        }`}></i>
                                                </div>
                                                <div className="tx-details">
                                                    <span className="tx-desc">{tx.description}</span>
                                                    <span className="tx-date">{new Date(tx.createdAt).toLocaleDateString()}</span>
                                                </div>
                                                <div className="tx-amount">
                                                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="no-transactions">Nenhuma transação encontrada.</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="rewards-section">
                                <h2>Prêmios Disponíveis</h2>
                                <div className="rewards-grid">
                                    {rewards.map(reward => (
                                        <div key={reward.id} className="reward-card">
                                            <div className="reward-icon" style={{ backgroundColor: reward.color || '#ddd' }}>
                                                <i className={`fas ${reward.icon || 'fa-gift'}`}></i>
                                            </div>
                                            <div className="reward-info">
                                                <h3>{reward.title}</h3>
                                                <p>{reward.description}</p>
                                                <div className="reward-cost">
                                                    <i className="fas fa-coins"></i> {reward.cost}
                                                </div>
                                            </div>
                                            <button
                                                className="btn-redeem"
                                                onClick={() => handleRedeemClick(reward)}
                                                disabled={(walletData?.wallet?.balance || 0) < reward.cost}
                                            >
                                                Resgatar
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Upload de Nota */}
            {showUploadModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Enviar Comprovante</h2>
                        <form onSubmit={handleSubmitReceipt}>
                            <div className="form-group">
                                <label>Loja</label>
                                <input type="text" value={storeName} onChange={e => setStoreName(e.target.value)} required placeholder="Ex: Amazon, Magalu..." />
                            </div>
                            <div className="form-group">
                                <label>Valor da Compra (R$)</label>
                                <input type="number" step="0.01" value={purchaseValue} onChange={e => setPurchaseValue(e.target.value)} required placeholder="0.00" />
                            </div>
                            <div className="form-group">
                                <label>Data da Compra</label>
                                <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} required />
                            </div>
                            <div className="form-group">
                                <label>Foto do Comprovante</label>
                                <input type="file" accept="image/*" onChange={handleImageUpload} />
                                {uploading && <p>Enviando imagem...</p>}
                                {receiptImage && <p className="success-msg">Imagem carregada!</p>}
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowUploadModal(false)} className="btn-cancel">Cancelar</button>
                                <button type="submit" className="btn-submit" disabled={uploading || !receiptImage}>Enviar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Resgate */}
            {showRedeemModal && selectedReward && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Confirmar Resgate</h2>
                        <div className="redeem-summary">
                            <p>Você está resgatando:</p>
                            <h3>{selectedReward.title}</h3>
                            <p className="cost">Custo: <strong>{selectedReward.cost} moedas</strong></p>
                        </div>

                        {selectedReward.id.includes('pix') ? (
                            <div className="form-group">
                                <label>Chave Pix</label>
                                <input
                                    type="text"
                                    value={pixKey}
                                    onChange={e => setPixKey(e.target.value)}
                                    placeholder="CPF, Email ou Telefone"
                                    required
                                />
                            </div>
                        ) : (
                            <div className="form-group">
                                <label>Email para envio (ou detalhes)</label>
                                <input
                                    type="text"
                                    value={redeemDetails}
                                    onChange={e => setRedeemDetails(e.target.value)}
                                    placeholder="Seu email ou instruções"
                                />
                            </div>
                        )}

                        <div className="modal-actions">
                            <button type="button" onClick={() => setShowRedeemModal(false)} className="btn-cancel">Cancelar</button>
                            <button type="button" onClick={confirmRedemption} className="btn-submit">Confirmar Resgate</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Wallet;
